import { QueryKey } from '@tanstack/react-query';
import {
  IntegrationEnum,
  IntegrationStatus,
  InternalIntegrationSetting,
} from 'models/InternalIntegrationSetting';
import useDisableIntegrationMutation, {
  IntegrationMutationLevel,
} from 'pages/integrations/hooks/useDisableIntegrationMutation';
import {
  INTEGRATIONS_REQUIRING_SHARE_GUEST_INFO,
  INTEGRATIONS_WITH_FREE_TRIAL,
} from 'pages/integrations/IntegrationsTabs.constants';
import useShareGuestInfoModal from 'pages/integrations/modals/shareGuestInfoModal/useShareGuestInfoModal';
import useDisableHostfullyDevicesModal from 'pages/integrations/modals/disableHostfullyDevicesModal/useDisableHostfullyDevicesModal';
import useRedirectToIntegrationDetails from 'pages/integrations/hooks/useRedirectToIntegrationDetails';
import useEnrollmentModal from 'pages/hostfullyAccounting/enrollmentModal/useEnrollmentModal';
import useDisableHostfullyAccountingModal from 'pages/integrations/modals/disableHostfullyAccountingModal/useDisableHostfullyAccountingModal';
import useDisableHostfullyAssistModal from 'pages/integrations/modals/disableHostfullyAssistModal/useDisableHostfullyAssistModal';
import useReenrollHostfullyAssistModal from 'pages/integrations/modals/reenrollHostfullyAssistModal/useReenrollHostfullyAssistModal';
import useActivateIntegration from './useActivateIntegration';

const useToggleSwitch = ({
  integrationSetting,
  queryKey,
}: {
  integrationSetting: InternalIntegrationSetting;
  queryKey: QueryKey;
}) => {
  const { disableIntegration } = useDisableIntegrationMutation();
  const { handleActivateIntegration } = useActivateIntegration();
  const { openShareGuestInfoModal } = useShareGuestInfoModal();
  const { openDisableHostfullyDevicesModal } =
    useDisableHostfullyDevicesModal();
  const { redirectToIntegrationDetails } = useRedirectToIntegrationDetails();
  const { open: openEnrollmentModal } = useEnrollmentModal();
  const { openDisableHostfullyAccountingModal } =
    useDisableHostfullyAccountingModal();
  const { openDisableHostfullyAssistModal } = useDisableHostfullyAssistModal();
  const { openReenrollHostfullyAssistModal } =
    useReenrollHostfullyAssistModal();

  const { enumId, integration, agencySettings } = integrationSetting;
  const { status, hasEnrolledBefore } = agencySettings;
  const isEnabled =
    status === IntegrationStatus.ACTIVE || status === IntegrationStatus.PENDING;
  const isHostfullyDevices =
    integrationSetting.enumId === IntegrationEnum.HOSTFULLYDEVICES;
  const isHostfullyAccounting = enumId === IntegrationEnum.HOSTFULLYACCOUNTING;
  const isHostfullyAssist = enumId === IntegrationEnum.HOSTFULLYASSIST;
  const isNeverEnrolledFreeTrial =
    INTEGRATIONS_WITH_FREE_TRIAL.includes(enumId) &&
    !isEnabled &&
    !hasEnrolledBefore;
  const isToggleDisabled =
    (isHostfullyAccounting && status === IntegrationStatus.PENDING) ||
    isNeverEnrolledFreeTrial;

  const handleDisable = () => {
    if (isHostfullyDevices) {
      openDisableHostfullyDevicesModal({
        integrationEnumId: enumId,
        queryKey,
      });

      return;
    }

    if (isHostfullyAccounting) {
      openDisableHostfullyAccountingModal({
        integrationEnumId: enumId,
        queryKey,
      });

      return;
    }

    if (isHostfullyAssist) {
      openDisableHostfullyAssistModal({
        integrationEnumId: enumId,
        queryKey,
      });

      return;
    }

    disableIntegration({
      level: IntegrationMutationLevel.AGENCY,
      enumId,
      queryKey,
    });
  };

  const activateAndRedirect = () => {
    handleActivateIntegration({
      integration,
      queryKey,
      onSuccess: () => {
        redirectToIntegrationDetails(integration);
      },
    });
  };

  const handleEnable = () => {
    if (isHostfullyAccounting) {
      openEnrollmentModal({ integration, queryKey });

      return;
    }

    if (isHostfullyAssist) {
      openReenrollHostfullyAssistModal({ integration, queryKey });

      return;
    }

    const needsShareGuestInfo =
      INTEGRATIONS_REQUIRING_SHARE_GUEST_INFO.includes(
        integrationSetting.enumId,
      );

    if (needsShareGuestInfo) {
      openShareGuestInfoModal({
        integration,
        onConfirm: () => {
          activateAndRedirect();
        },
      });

      return;
    }

    activateAndRedirect();
  };

  const handleToggle = () => {
    if (isEnabled) {
      handleDisable();
    } else {
      handleEnable();
    }
  };

  return { handleToggle, isToggleDisabled };
};

export default useToggleSwitch;
